import { fetchAccount } from "@/services/account.queries";
import { fetchPay } from "@/services/pay.queries";
import { useToastNotify } from "@/stores/toastStore";
import { UNKNOWN_VALUE, isEmail } from "@/utils/etc";
import { getValueFromStorage, saveToStorage } from "@/utils/secureStore";
import { useIsFocused } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@transifex/react";
import { CheckoutFailReason } from "ipctg-inflow-types/InFlowEnums";
import type { ParkPayPOSTSuccessResponse } from "ipctg-inflow-types/InFlowParkAPI";
import { useState } from "react";
import { useRefreshOnFocus } from "./useRefreshOnFocus";

interface LastPay {
  payResponse: ParkPayPOSTSuccessResponse;
  termsAndConditionsOKTimestamp: string;
}

export const usePay = (enabled: boolean, termsAndConditionsOKTimestamp: string, stripeSessionId: string | undefined) => {
  /*
    There is an underlying bug where just calling notify
    causes an infinite loop. Likely to do something with
    how the toasts are populated. In the meantime we are
    papering over the bug with this state.
  */
  const [isErrorNotified, setErrorNotified] = useState(false);
  const t = useT();
  const { notify } = useToastNotify();
  const isFocused = useIsFocused();

  const { data, isFetching, error, refetch } = useQuery({
    enabled: enabled && isFocused,
    queryKey: ["pay", termsAndConditionsOKTimestamp],
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      /*
        Using idToken directly from localStorage since signing
        out causes this function to be run again and we don't
        want that
      */
      const idToken = await getValueFromStorage("idToken");
      const storedContact = await getValueFromStorage("contact");
      const storedLastPay = await getValueFromStorage("lastPay");

      const storedLastPayInstance = storedLastPay ? (JSON.parse(storedLastPay) as LastPay) : null;
      if (termsAndConditionsOKTimestamp === storedLastPayInstance?.termsAndConditionsOKTimestamp) {
        return storedLastPayInstance?.payResponse;
      }

      let contact = storedContact ?? "";
      if (!contact && idToken) {
        try {
          const account = await fetchAccount({ cognitoIDToken: idToken ?? "" });
          contact = account.phone;
        } catch (e: unknown) {
          // We don't want this error and just pass UNKNOWN for the phone number
          console.error(e);
        }
      }

      const rsp = await fetchPay({
        cognitoIDToken: idToken,
        stripeCheckoutSessionID: stripeSessionId ?? null,
        phoneNumber: contact ? (!isEmail(contact) ? contact : null) : UNKNOWN_VALUE,
        email: isEmail(contact) ? contact : null,
        termsAndConditionsOKTimestamp,
      });

      const dataToSave = JSON.stringify({
        payResponse: rsp,
        termsAndConditionsOKTimestamp,
      });
      await saveToStorage("lastPay", dataToSave);

      return rsp;
    },
  });

  useRefreshOnFocus(refetch, enabled && isFocused);

  if (isFetching) {
    return { data: undefined, isPending: true };
  }

  if (error && !isErrorNotified) {
    if ("failReason" in error && error.failReason === CheckoutFailReason.SessionAlreadyPaid) {
      setErrorNotified(true);
      notify({
        severity: "warning",
        message: t("This session has already been paid for."),
      });
    } else {
      setErrorNotified(true);
      notify({
        severity: "error",
        message: t("An error occurred when fetching pay data"),
      });
    }
    console.error("Error fetching pay data:", error);
    return { data: undefined, isPending: false };
  }

  return { data, isPending: false };
};
